import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
} from "@mui/material";
import { ProjectStatus } from "@repo/server-api";
import { useNotification } from "@repo/shared/components";
import { gql, useMutation } from "@apollo/client";
import { useTranslation } from "next-i18next";
import React, { forwardRef, useImperativeHandle, useState } from "react";

const UPDATE_PROJECT_STATUS = gql`
	mutation UpdateProjectStatus($projectId: ID!, $newStatus: ProjectStatus!) {
		updateProjectStatus(input: { projectId: $projectId, newStatus: $newStatus }) {
			project {
				id
				status
				isManualStatusOverride
			}
			errors {
				... on DomainError {
					errorType
					message
				}
				... on PropertyValidationError {
					errorType
					message
					errors {
						errorMessage
						propertyName
					}
				}
			}
		}
	}
`;

const RESET_PROJECT_STATUS = gql`
	mutation ResetProjectStatusToAutomatic($projectId: ID!) {
		resetProjectStatusToAutomatic(input: { projectId: $projectId }) {
			project {
				id
				status
				isManualStatusOverride
			}
			errors {
				... on DomainError {
					errorType
					message
				}
				... on PropertyValidationError {
					errorType
					message
					errors {
						errorMessage
						propertyName
					}
				}
			}
		}
	}
`;

const allStatuses = [
	ProjectStatus.Draft,
	ProjectStatus.OfferCreated,
	ProjectStatus.OfferSent,
	ProjectStatus.Active,
	ProjectStatus.Rejected,
	ProjectStatus.Completed,
	ProjectStatus.Canceled,
];

const statusColor: Record<ProjectStatus, string> = {
	[ProjectStatus.Draft]: "#9E9E9E",
	[ProjectStatus.OfferCreated]: "#FFC107",
	[ProjectStatus.OfferSent]: "#FF9800",
	[ProjectStatus.Active]: "#4CAF50",
	[ProjectStatus.Rejected]: "#F44336",
	[ProjectStatus.Canceled]: "#616161",
	[ProjectStatus.Completed]: "#607D8B",
};

export type DialogProjectChangeStatusHandle = {
	show: (projectId: string, projectName: string, currentStatus: ProjectStatus, isManualOverride: boolean) => void;
};

type DialogProjectChangeStatusProps = {
	onStatusChanged?: () => void;
};

export const DialogProjectChangeStatus = forwardRef<
	DialogProjectChangeStatusHandle,
	DialogProjectChangeStatusProps
>(({ onStatusChanged }, ref) => {
	const { t } = useTranslation();
	const { showNotification } = useNotification();

	const [open, setOpen] = useState(false);
	const [projectId, setProjectId] = useState<string | null>(null);
	const [projectName, setProjectName] = useState("");
	const [currentStatus, setCurrentStatus] = useState<ProjectStatus>(ProjectStatus.Draft);
	const [isManualOverride, setIsManualOverride] = useState(false);
	const [newStatus, setNewStatus] = useState<ProjectStatus | "">("");

	const [updateStatus, { loading: updateLoading }] = useMutation(UPDATE_PROJECT_STATUS, {
		onCompleted(result) {
			const errors = result.updateProjectStatus?.errors;
			if (errors?.length) {
				errors.forEach((e: { message: string }) => showNotification(e.message, "error"));
				return;
			}
			showNotification(t("project-status-changed-successfully"), "success");
			setOpen(false);
			onStatusChanged?.();
		},
		onError(error) {
			showNotification(error.message, "error");
		},
	});

	const [resetStatus, { loading: resetLoading }] = useMutation(RESET_PROJECT_STATUS, {
		onCompleted(result) {
			const errors = result.resetProjectStatusToAutomatic?.errors;
			if (errors?.length) {
				errors.forEach((e: { message: string }) => showNotification(e.message, "error"));
				return;
			}
			showNotification(t("project-status-reset-successfully"), "success");
			setOpen(false);
			onStatusChanged?.();
		},
		onError(error) {
			showNotification(error.message, "error");
		},
	});

	const loading = updateLoading || resetLoading;

	useImperativeHandle(ref, () => ({
		show: (pid: string, pname: string, status: ProjectStatus, manualOverride: boolean) => {
			setProjectId(pid);
			setProjectName(pname);
			setCurrentStatus(status);
			setIsManualOverride(manualOverride);
			setNewStatus("");
			setOpen(true);
		},
	}));

	const availableStatuses = allStatuses.filter((s) => s !== currentStatus);

	const handleSave = () => {
		if (!projectId || !newStatus) return;
		updateStatus({ variables: { projectId, newStatus } });
	};

	const handleReset = () => {
		if (!projectId) return;
		resetStatus({ variables: { projectId } });
	};

	return (
		<Dialog
			open={open}
			onClose={() => setOpen(false)}
			maxWidth="xs"
			fullWidth
			data-testid="change-status-dialog"
		>
			<DialogTitle>{t("change-project-status")}</DialogTitle>
			<DialogContent>
				<Box display="flex" flexDirection="column" gap={2} pt={1}>
					<Box>
						<strong>{projectName}</strong>
					</Box>
					<Box display="flex" alignItems="center" gap={1}>
						{t("current-status")}:
						<Chip
							label={t(`project-status.${currentStatus.toLowerCase()}`)}
							size="small"
							sx={{
								backgroundColor: statusColor[currentStatus],
								color: "#fff",
							}}
						/>
						{isManualOverride && (
							<Chip
								label={t("manual-override")}
								size="small"
								variant="outlined"
								color="warning"
							/>
						)}
					</Box>
					{isManualOverride && (
						<Alert
							severity="info"
							action={
								<Button
									size="small"
									onClick={handleReset}
									disabled={loading}
									data-testid="reset-status-button"
								>
									{resetLoading ? <CircularProgress size={16} /> : t("reset-to-automatic")}
								</Button>
							}
						>
							{t("manual-override-info")}
						</Alert>
					)}
					<FormControl fullWidth size="small">
						<InputLabel>{t("new-status")}</InputLabel>
						<Select
							value={newStatus}
							label={t("new-status")}
							onChange={(e) => setNewStatus(e.target.value as ProjectStatus)}
							data-testid="new-status-select"
						>
							{availableStatuses.map((status) => (
								<MenuItem key={status} value={status}>
									<Chip
										label={t(`project-status.${status.toLowerCase()}`)}
										size="small"
										sx={{
											backgroundColor: statusColor[status],
											color: "#fff",
										}}
									/>
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => setOpen(false)}>{t("cancel")}</Button>
				<Button
					variant="contained"
					onClick={handleSave}
					disabled={!newStatus || loading}
					data-testid="save-status-button"
				>
					{updateLoading ? <CircularProgress size={20} /> : t("save")}
				</Button>
			</DialogActions>
		</Dialog>
	);
});

DialogProjectChangeStatus.displayName = "DialogProjectChangeStatus";
